// Normalizes transactions + executed trades into the League Wire's ledger entries.
import { LeagueTransaction, Trade } from '../types';

export type WireEntry =
  | { kind: 'pickup' | 'drop'; ts: string; teamName: string; playerName: string;
      playerPosition: string; nflTeam: string; week: number | null }
  | { kind: 'trade'; ts: string; teamAName: string; teamBName: string;
      aReceives: { name: string; position: string }[];
      bReceives: { name: string; position: string }[] };

const WIRE_LIMIT = 10;

export function buildWireEntries(transactions: LeagueTransaction[], trades: Trade[]): WireEntry[] {
  const txnEntries: WireEntry[] = transactions
    .filter(t => t.processed_at && t.details?.player_name && t.details?.team_name)
    .map(t => ({
      kind: t.transaction_type,
      ts: t.processed_at,
      teamName: t.details.team_name,
      playerName: t.details.player_name,
      playerPosition: t.details.player_position || '',
      nflTeam: t.details.nfl_team || '',
      week: t.week ?? null,
    }));

  // NOTE the explicit callback return annotation: without it, TS infers
  // position as the 'K'|'P' literal union and the type predicate below fails
  // TS2677 under strict mode (Jest won't catch it — the build is the type gate).
  const tradeEntries: WireEntry[] = trades
    .filter(t => t.status === 'executed' && t.items.some(i => i.player))
    .map((t): Extract<WireEntry, { kind: 'trade' }> | null => {
      const ts = t.executed_at ?? t.responded_at ?? t.proposed_at;
      if (!ts) return null;
      const side = (teamId: number) =>
        t.items
          .filter(i => i.to_team_id === teamId && i.player)
          .map(i => ({ name: i.player!.name, position: i.player!.position }));
      return {
        kind: 'trade',
        ts,
        teamAName: t.proposer_team?.team_name ?? 'Team',
        teamBName: t.recipient_team?.team_name ?? 'Team',
        aReceives: side(t.proposer_team_id),
        bReceives: side(t.recipient_team_id),
      };
    })
    .filter((e): e is Extract<WireEntry, { kind: 'trade' }> => e !== null);

  // Cross-feed ordering relies on both feeds emitting timezone-qualified ISO strings
  // (transactions get "Z" appended server-side in waivers.py; trades serialize with
  // +00:00) — a naive string would parse as local time and skew ordering.
  return [...txnEntries, ...tradeEntries]
    .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
    .slice(0, WIRE_LIMIT);
}
